import { EventMsgError } from "./event-msg-error.js";

/**
 * Error related to EventMsgV3 protocol operations (encoding, decoding, validation)
 */
export class ProtocolError extends EventMsgError {
  constructor(
    message: string,
    options: {
      context?: Record<string, unknown>;
      solutions?: string[];
      cause?: Error;
    } = {}
  ) {
    super(message, "PROTOCOL_ERROR", {
      ...options,
      solutions: options.solutions || [
        "Check message format",
        "Verify protocol specification compliance",
        "Review message content",
      ],
    });
  }
}

/**
 * Message encoding failed
 */
export class EncodingError extends ProtocolError {
  constructor(
    message = "Failed to encode message",
    options: {
      context?: Record<string, unknown>;
      cause?: Error;
    } = {}
  ) {
    super(message, {
      ...options,
      solutions: [
        "Check event name length (≤64 bytes)",
        "Check event data length (≤3048 bytes)",
        "Verify data is JSON serializable",
        "Check for invalid characters",
      ],
    });

    this.code = "ENCODING_ERROR";
  }
}

/**
 * Message decoding failed
 */
export class DecodingError extends ProtocolError {
  constructor(
    message = "Failed to decode message",
    options: {
      context?: Record<string, unknown>;
      cause?: Error;
    } = {}
  ) {
    super(message, {
      ...options,
      solutions: [
        "Check message framing (SOH, STX, US, EOT)",
        "Verify byte stuffing is correct",
        "Check for data corruption",
        "Ensure sender uses same protocol version",
      ],
    });

    this.code = "DECODING_ERROR";
  }
}

/**
 * Message validation failed
 */
export class ValidationError extends ProtocolError {
  constructor(
    message = "Message validation failed",
    options: {
      context?: Record<string, unknown>;
      cause?: Error;
    } = {}
  ) {
    super(message, {
      ...options,
      solutions: [
        "Check address ranges (0-255)",
        "Verify message size limits",
        "Check required fields are present",
        "Validate data types",
      ],
    });

    this.code = "VALIDATION_ERROR";
  }
}

/**
 * Invalid message format
 */
export class InvalidMessageError extends ProtocolError {
  constructor(
    message = "Invalid message format",
    options: {
      context?: Record<string, unknown>;
      cause?: Error;
    } = {}
  ) {
    super(message, {
      ...options,
      solutions: [
        "Check message starts with SOH (0x01)",
        "Check message ends with EOT (0x04)",
        "Verify minimum message length (10 bytes)",
        "Check header is exactly 7 bytes after unstuffing",
      ],
    });

    this.code = "INVALID_MESSAGE_ERROR";
  }
}
